import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ChevronDown, ChevronRight, Search } from "./icons"

// The wiki's shape, drawn from the paths.
//
// There is no folder entity and there is not going to be one — a folder exists
// because pages share a prefix, which is what makes moving one a rename rather
// than a graph operation. So the tree is derived here, from the list the pages
// endpoint already returns, and nothing has to be kept in sync.

export type TreePage = { id: string; path: string; title: string }

type Node = {
  name: string
  path: string
  page: TreePage | null
  children: Node[]
  /** Pages at or beneath this node. */
  count: number
}

function build(pages: TreePage[]): Node[] {
  const root: Node = { name: "", path: "", page: null, children: [], count: 0 }

  for (const page of pages) {
    const segments = page.path.split("/").filter(Boolean)
    let node = root

    segments.forEach((segment, index) => {
      const isLeaf = index === segments.length - 1
      const path = segments.slice(0, index + 1).join("/")
      let next = node.children.find((child) => child.name === segment)
      if (!next) {
        next = { name: segment, path, page: null, children: [], count: 0 }
        node.children.push(next)
      }
      // A page can also be a folder — `guides/index.md` alongside `guides/a.md`
      // is one path that is both.
      if (isLeaf) next.page = page
      node = next
    })
  }

  const tally = (node: Node): number => {
    node.count = (node.page ? 1 : 0) + node.children.reduce((sum, child) => sum + tally(child), 0)
    // Folders first, then pages, each alphabetically — the order a file tree
    // is read in.
    node.children.sort((a, b) => {
      const aFolder = a.children.length > 0
      const bFolder = b.children.length > 0
      if (aFolder !== bFolder) return aFolder ? -1 : 1
      return a.name.localeCompare(b.name)
    })
    return node.count
  }
  tally(root)

  return root.children
}

/** Folders holding the current page, so opening a page opens the path to it. */
function ancestorsOf(path: string | undefined): Set<string> {
  const out = new Set<string>()
  if (!path) return out
  const segments = path.split("/").filter(Boolean)
  for (let i = 1; i < segments.length; i++) out.add(segments.slice(0, i).join("/"))
  return out
}

export function PageTree({
  pages,
  currentId,
  currentPath,
}: {
  pages: TreePage[]
  currentId?: string
  currentPath?: string
}) {
  const [filter, setFilter] = useState("")
  const [closed, setClosed] = useState<Set<string>>(new Set())

  const matching = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    if (!needle) return pages
    return pages.filter(
      (page) =>
        page.title.toLowerCase().includes(needle) || page.path.toLowerCase().includes(needle),
    )
  }, [pages, filter])

  const tree = useMemo(() => build(matching), [matching])
  const open = ancestorsOf(currentPath)

  // Filtering opens everything: a match three folders deep is useless if the
  // reader has to guess which folders to expand to reach it.
  const isOpen = (path: string) => (filter.trim() ? true : !closed.has(path) || open.has(path))

  const toggle = (path: string) =>
    setClosed((current) => {
      const next = new Set(current)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })

  return (
    <nav aria-label="Pages" className="flex flex-col gap-2">
      <label className="relative flex items-center">
        <Search className="pointer-events-none absolute left-2.5 size-3.5 text-faint" />
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter pages"
          aria-label="Filter pages"
          className="w-full rounded-md border border-border bg-surface py-1.5 pr-2 pl-8 text-xs outline-none placeholder:text-faint focus:border-interactive"
        />
      </label>

      {tree.length === 0 ? (
        <p className="px-2 py-4 text-xs text-faint">
          {filter.trim() ? "Nothing matches" : "No pages yet"}
        </p>
      ) : (
        <ul className="flex flex-col">
          {tree.map((node) => (
            <TreeNode
              key={node.path}
              node={node}
              depth={0}
              currentId={currentId}
              isOpen={isOpen}
              toggle={toggle}
            />
          ))}
        </ul>
      )}
    </nav>
  )
}

function TreeNode({
  node,
  depth,
  currentId,
  isOpen,
  toggle,
}: {
  node: Node
  depth: number
  currentId?: string
  isOpen: (path: string) => boolean
  toggle: (path: string) => void
}) {
  const isFolder = node.children.length > 0
  const expanded = isFolder && isOpen(node.path)
  const active = node.page?.id === currentId
  // 12px a level, plus room for the chevron a page does not have.
  const indent = { paddingLeft: `${depth * 0.75 + (isFolder ? 0 : 1.1)}rem` }

  return (
    <li>
      <div
        className={`group flex items-center gap-1 rounded px-1 py-1 text-xs transition-colors ${
          active ? "bg-accent-soft text-accent" : "text-muted hover:bg-surface-muted"
        }`}
        style={indent}
      >
        {isFolder && (
          <button
            type="button"
            onClick={() => toggle(node.path)}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${node.name}`}
            className="shrink-0 text-faint hover:text-foreground"
          >
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        )}

        {node.page ? (
          <Link to={`/context-hub/pages/${node.page.id}`} className="min-w-0 flex-1 truncate">
            {node.page.title || node.name}
          </Link>
        ) : (
          // A folder with no page of its own is a label, not a destination.
          <button
            type="button"
            onClick={() => toggle(node.path)}
            className="min-w-0 flex-1 truncate text-left font-medium text-foreground"
          >
            {node.name}
          </button>
        )}

        {isFolder && (
          <span className="shrink-0 font-mono text-[10px] text-faint tabular-nums">{node.count}</span>
        )}
      </div>

      {expanded && (
        <ul className="flex flex-col">
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              currentId={currentId}
              isOpen={isOpen}
              toggle={toggle}
            />
          ))}
        </ul>
      )}
    </li>
  )
}
